import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel } from '../components/ui'
import { thaiDateTime } from '../lib/format'

// Contract with handlers_provision.go (part 125):
//   GET  /v1/provisioning/runs     -> { steps, settings }
//   POST /v1/provisioning/tenants  { slug, legal_name, admin_email, admin_password, admin_name,
//                                    plan, timezone, locale } -> { tenant, settings, steps }
// The slug is the idempotency key: resubmitting a slug that already exists resumes the
// unfinished steps instead of creating a second organisation.

type StepStatus = 'pending' | 'done' | 'failed'

interface ProvisionStep {
  step: string
  label: string
  status: StepStatus
  detail?: string
  created_at?: string
}

interface TenantSettings {
  plan_key: string
  timezone: string
  locale: string
  seeded: boolean
  onboarded_at?: string
}

interface RunsView {
  steps: ProvisionStep[]
  settings?: TenantSettings
}

interface ProvisionTenant {
  id: string
  slug: string
  legal_name: string
  display_name: string
  status: string
  timezone: string
  locale: string
  base_currency: string
  created_at: string
}

interface ProvisionResult {
  tenant: ProvisionTenant
  settings?: TenantSettings
  steps: ProvisionStep[]
  already_exists: boolean
}

const statusLabel: Record<StepStatus, string> = {
  pending: 'รอดำเนินการ',
  done: 'เสร็จสิ้น',
  failed: 'ล้มเหลว',
}

const statusBadge: Record<StepStatus, string> = {
  pending: 'received',
  done: 'closed',
  failed: 'reviewing',
}

const statusColor: Record<StepStatus, string> = {
  pending: 'var(--color-ivory-dim)',
  done: 'var(--color-emerald-soft)',
  failed: 'var(--color-crimson)',
}

const plans = [
  { key: 'starter', label: 'Starter' },
  { key: 'growth', label: 'Growth' },
  { key: 'enterprise', label: 'Enterprise' },
]

function Timeline({ steps }: { steps: ProvisionStep[] }) {
  const done = steps.filter((s) => s.status === 'done').length
  return (
    <div className="card card-pad" style={{ marginTop: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div className="card-label">ลำดับการติดตั้ง</div>
        <span className="dim tnum" style={{ fontSize: 11.5 }}>{done}/{steps.length} ขั้นตอน</span>
      </div>
      <ol style={{ listStyle: 'none', margin: '18px 0 0', padding: 0 }}>
        {steps.map((s, i) => (
          <li key={s.step} style={{ display: 'flex', gap: 12, paddingBottom: i === steps.length - 1 ? 0 : 16, position: 'relative' }}>
            {i < steps.length - 1 && (
              <span style={{
                position: 'absolute', left: 10, top: 24, bottom: 0, width: 1,
                background: 'var(--color-hairline)',
              }} />
            )}
            <span style={{
              width: 21, height: 21, borderRadius: 999, flex: '0 0 auto', zIndex: 1,
              display: 'grid', placeItems: 'center', fontSize: 10.5, fontWeight: 600,
              color: s.status === 'pending' ? 'var(--color-ivory-dim)' : '#14110B',
              background: s.status === 'pending' ? 'var(--color-wash)' : statusColor[s.status],
              border: `1px solid ${s.status === 'pending' ? 'var(--color-hairline)' : statusColor[s.status]}`,
            }}>
              {s.status === 'done' ? '✓' : s.status === 'failed' ? '!' : i + 1}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="row" style={{ justifyContent: 'space-between', gap: 10 }}>
                <b style={{ fontWeight: 500, fontSize: 12.5 }}>{s.label}</b>
                <span className={`badge badge-${statusBadge[s.status]}`}>{statusLabel[s.status]}</span>
              </div>
              {s.detail && <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>{s.detail}</div>}
              {s.created_at && <div className="dim mono" style={{ fontSize: 10.5, marginTop: 4 }}>{thaiDateTime(s.created_at)}</div>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  )
}

export function Provisioning() {
  const { me } = useAuth()
  const canProvision = me?.role === 'owner'
  const qc = useQueryClient()

  const runs = useQuery({
    queryKey: ['provisioning'],
    queryFn: () => api<RunsView>('/v1/provisioning/runs'),
    enabled: canProvision,
  })

  const [slug, setSlug] = useState('')
  const [legalName, setLegalName] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [adminPassword, setAdminPassword] = useState('')
  const [adminName, setAdminName] = useState('')
  const [plan, setPlan] = useState('starter')
  const [timezone, setTimezone] = useState('Asia/Bangkok')
  const [locale, setLocale] = useState('th-TH')
  const [err, setErr] = useState('')
  const [result, setResult] = useState<ProvisionResult | null>(null)

  const create = useMutation({
    mutationFn: () => apiWrite<ProvisionResult>('/v1/provisioning/tenants', 'POST', {
      slug, legal_name: legalName, admin_email: adminEmail, admin_password: adminPassword,
      admin_name: adminName, plan, timezone, locale,
    }),
    onSuccess: (data) => {
      setErr('')
      setAdminPassword('')
      setResult(data)
      void qc.invalidateQueries({ queryKey: ['provisioning'] })
    },
    onError: (e) => setErr((e as Error).message),
  })

  if (!canProvision) {
    return (
      <>
        <PageHead title="Provisioning องค์กรใหม่" sub="สร้างองค์กรและผู้ดูแลคนแรกจากหน้าจอเดียว" />
        <Empty title="เฉพาะเจ้าขององค์กร" body="การสร้างองค์กรใหม่ผูกกับบัญชีเจ้าของเท่านั้น กรุณาให้ owner ขององค์กรเป็นผู้ดำเนินการ" />
      </>
    )
  }

  const ready = slug.trim().length >= 3 && legalName.trim() !== '' && adminEmail.trim() !== '' && adminPassword.length >= 12
  const history = runs.data?.steps ?? []

  return (
    <>
      <PageHead
        title="Provisioning องค์กรใหม่"
        sub="สร้างองค์กร ผู้ดูแลคนแรก ลูกค้าตัวอย่าง และค่าเริ่มต้นของแพลตฟอร์ม โดยไม่ต้องรอทีมปฏิบัติการ"
        actions={<span className="badge badge-received">owner only</span>}
      />

      <form className="card card-pad" onSubmit={(e) => { e.preventDefault(); setErr(''); create.mutate() }}>
        <div className="card-label">ข้อมูลองค์กรและผู้ดูแล</div>
        <div className="grid-2" style={{ marginTop: 12 }}>
          <div className="field" style={{ marginTop: 0 }}>
            <label htmlFor="p-slug">Slug องค์กร</label>
            <input id="p-slug" className="input mono" value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
              placeholder="demo-two" autoComplete="off" spellCheck={false} />
            <div className="dim" style={{ fontSize: 10.5, marginTop: 5 }}>
              ตัวพิมพ์เล็ก 3–32 ตัว ขึ้นต้นด้วยตัวอักษร ใช้ a-z, 0-9 และ -
            </div>
          </div>
          <div className="field" style={{ marginTop: 0 }}>
            <label htmlFor="p-legal">ชื่อนิติบุคคล</label>
            <input id="p-legal" className="input" value={legalName}
              onChange={(e) => setLegalName(e.target.value)} placeholder="บริษัท ตัวอย่าง จำกัด" />
          </div>
          <div className="field" style={{ marginTop: 0 }}>
            <label htmlFor="p-email">อีเมลผู้ดูแลระบบ</label>
            <input id="p-email" className="input" type="email" value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)} placeholder="owner@example.com"
              autoComplete="off" spellCheck={false} />
          </div>
          <div className="field" style={{ marginTop: 0 }}>
            <label htmlFor="p-password">รหัสผ่านผู้ดูแลระบบ</label>
            <input id="p-password" className="input" type="password" value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)} placeholder="อย่างน้อย 12 ตัว มีทั้งตัวอักษรและตัวเลข"
              autoComplete="new-password" />
          </div>
          <div className="field" style={{ marginTop: 0 }}>
            <label htmlFor="p-admin-name">ชื่อที่แสดงของผู้ดูแล</label>
            <input id="p-admin-name" className="input" value={adminName}
              onChange={(e) => setAdminName(e.target.value)} placeholder="ผู้ดูแลระบบ" />
          </div>
          <div className="field" style={{ marginTop: 0 }}>
            <label htmlFor="p-plan">แพ็กเกจ</label>
            <select id="p-plan" className="select" style={{ height: 42, width: '100%' }} value={plan} onChange={(e) => setPlan(e.target.value)}>
              {plans.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div className="field" style={{ marginTop: 0 }}>
            <label htmlFor="p-tz">เขตเวลา</label>
            <input id="p-tz" className="input mono" value={timezone}
              onChange={(e) => setTimezone(e.target.value)} placeholder="Asia/Bangkok" />
          </div>
          <div className="field" style={{ marginTop: 0 }}>
            <label htmlFor="p-locale">ภาษา</label>
            <input id="p-locale" className="input mono" value={locale}
              onChange={(e) => setLocale(e.target.value)} placeholder="th-TH" />
          </div>
        </div>
        {err && <div className="form-error">{err}</div>}
        <div className="row" style={{ gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" type="submit" disabled={create.isPending || !ready}>
            {create.isPending ? 'กำลังติดตั้ง…' : 'สร้างองค์กร'}
          </button>
          <span className="dim" style={{ fontSize: 11.5 }}>
            ใช้ slug เป็นกุญแจกันสร้างซ้ำ — ส่งคำขอเดิมอีกครั้งจะทำขั้นตอนที่ค้างให้จบ
          </span>
        </div>
      </form>

      {result && (
        <>
          <div className="row" style={{ gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
            <span className={`badge badge-${result.already_exists ? 'received' : 'closed'}`}>
              {result.already_exists ? 'มีองค์กรนี้อยู่แล้ว' : 'สร้างองค์กรสำเร็จ'}
            </span>
            <span className="mono" style={{ fontSize: 12 }}>{result.tenant.slug}</span>
            <span className="dim" style={{ fontSize: 11.5 }}>
              {result.tenant.legal_name} · {result.tenant.status}
            </span>
          </div>
          {result.settings && (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
              แพ็กเกจ {result.settings.plan_key} · {result.settings.timezone} · {result.settings.locale}
              {result.settings.onboarded_at ? ` · onboarded ${thaiDateTime(result.settings.onboarded_at)}` : ''}
            </div>
          )}
          <Timeline steps={result.steps} />
        </>
      )}

      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginTop: 24, marginBottom: 10 }}>
        <div className="card-label">ประวัติการติดตั้งขององค์กรนี้</div>
        {runs.data?.settings && (
          <span className="dim" style={{ fontSize: 11 }}>
            seeded: {runs.data.settings.seeded ? 'yes' : 'no'}
          </span>
        )}
      </div>

      {runs.isLoading ? <Skel h={160} /> : history.length === 0 ? (
        <Empty title="ยังไม่มีประวัติการติดตั้ง" body="ใช้ฟอร์มด้านบนเพื่อสร้างองค์กรใหม่ ลำดับขั้นตอนจะปรากฏที่นี่" />
      ) : (
        <div className="table-wrap card">
          <table className="data">
            <thead>
              <tr>
                <th>ขั้นตอน</th><th>สถานะ</th><th>รายละเอียด</th><th className="num">เวลา</th>
              </tr>
            </thead>
            <tbody>
              {history.map((s) => (
                <tr key={s.step} style={{ cursor: 'default' }}>
                  <td><span className="mono" style={{ fontSize: 11 }}>{s.step}</span><div style={{ marginTop: 3 }}>{s.label}</div></td>
                  <td><span className={`badge badge-${statusBadge[s.status]}`}>{statusLabel[s.status]}</span></td>
                  <td className="dim">{s.detail || '—'}</td>
                  <td className="num dim">{s.created_at ? thaiDateTime(s.created_at) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
